<script setup>
import { ref, watch, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'

const { t } = useI18n()
const { setting } = useCurrency()

const props = defineProps({
  open:      { type: Boolean, required: true },
  vehicleId: { type: Number,  required: true },
  rentalId:  { type: Number,  default: null },
  damage:    { type: Object,  default: null },
})

const emit = defineEmits(['close'])

const form = useForm({
  description:       '',
  severity:          'minor',
  occurred_on:       new Date().toISOString().slice(0, 10),
  repair_cost_cents: 0,
  rental_id:         null,
  photos:            [],
})

const isEdit = computed(() => !!props.damage?.id)

watch(() => props.open, (open) => {
  if (open) {
    if (props.damage) {
      form.description       = props.damage.description
      form.severity          = props.damage.severity
      form.occurred_on       = props.damage.occurred_on
      form.repair_cost_cents = props.damage.repair_cost_cents
      form.rental_id         = props.damage.rental_id
    } else {
      form.reset()
      form.rental_id = props.rentalId
    }
    form.photos = []
    form.clearErrors()
  }
})

const repairDollars = computed({
  get: () => (form.repair_cost_cents / 100).toFixed(2),
  set: (v) => { form.repair_cost_cents = Math.round(parseFloat(v) * 100) || 0 }
})

const severityOptions = computed(() => [
  { value: 'minor',    label: t('damage.severity.minor') },
  { value: 'moderate', label: t('damage.severity.moderate') },
  { value: 'severe',   label: t('damage.severity.severe') },
])

const onPhotoInput = (e) => {
  form.photos = Array.from(e.target.files || [])
}

const submit = () => {
  if (isEdit.value) {
    form.transform(d => ({ damage: d })).patch(`/damages/${props.damage.id}`, {
      forceFormData: true,
      onSuccess: () => emit('close')
    })
  } else {
    form.transform(d => ({ damage: d })).post(`/vehicles/${props.vehicleId}/damages`, {
      forceFormData: true,
      onSuccess: () => emit('close')
    })
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60" @click.self="$emit('close')">
      <div class="w-full h-full md:h-auto md:max-w-lg md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
        <header class="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[color:var(--line)]">
          <h2 class="text-base fw-value text-[color:var(--ink)]">
            {{ isEdit ? t('damage.modal.editTitle') : t('damage.modal.reportTitle') }}
          </h2>
          <button class="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]" type="button" aria-label="Close" @click="$emit('close')">
            <Icon name="x" :size="18" />
          </button>
        </header>

        <form id="report-damage-form" class="flex-1 overflow-y-auto px-4 py-4 space-y-4" @submit.prevent="submit">
          <div>
            <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.severity') }}</label>
            <UiSegmented v-model="form.severity" :options="severityOptions" />
          </div>

          <div>
            <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.description') }} *</label>
            <UiTextarea v-model="form.description" :rows="3" :error="form.errors.description" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.occurredOn') }} *</label>
              <UiInput v-model="form.occurred_on" type="date" :error="form.errors.occurred_on" />
            </div>
            <div>
              <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.repairCost') }} ({{ setting.currency_code }})</label>
              <UiInput v-model="repairDollars" type="number" step="0.01" :error="form.errors.repair_cost_cents" />
            </div>
          </div>

          <div>
            <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.photos') }}</label>
            <input
              type="file"
              multiple
              accept="image/*"
              class="block w-full text-sm text-[color:var(--ink)] file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:px-3 file:py-1.5 file:bg-[color:var(--surfaceAlt)] file:text-[color:var(--ink)]"
              @change="onPhotoInput"
            />
            <p v-if="form.photos.length" class="mt-1 text-xs text-[color:var(--muted)]">{{ form.photos.length }} {{ t('damage.photosSelected') }}</p>
            <p v-if="form.errors.photos" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.photos }}</p>
          </div>
        </form>

        <footer class="flex-shrink-0 px-4 py-3 border-t border-[color:var(--line)] flex items-center justify-end gap-2 pb-[max(env(safe-area-inset-bottom),0.75rem)] md:pb-3">
          <UiButton variant="ghost" type="button" @click="$emit('close')">{{ t('damage.cancel') }}</UiButton>
          <UiButton variant="primary" type="button" :loading="form.processing" @click="submit">
            {{ t('damage.modal.saveButton') }}
          </UiButton>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
